export interface TransformScriptBindingExpr {
  name: string;
  args: string[];
  raw: string;
}

export interface TransformScriptBindingContext {
  selfValue: string;
  hasSelf: boolean;
  refs: Record<string, string>;
  nowEpochMilliseconds?: number;
}
import {
  formatIsoDate,
  formatIsoDateTime,
  formatIsoDateTimeMs,
  getNowParts
} from './temporal.js';

function parsePositiveInt(raw: string) {
  const parsed = Number.parseInt(raw.trim(), 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
}

function randomLengthInRange(min: number, max: number) {
  if (min === max) {
    return min;
  }

  const span = max - min + 1;
  const bytes = new Uint32Array(1);
  crypto.getRandomValues(bytes);
  return min + (bytes[0]! % span);
}

function randomString(length: number) {
  if (length <= 0) {
    return '';
  }

  const alphabet = 'abcdefghijklmnopqrstuvwxyz0123456789';
  const bytes = new Uint8Array(length);
  crypto.getRandomValues(bytes);

  return Array.from(bytes, byte => alphabet[byte % alphabet.length] ?? '').join('');
}

function resolveBuiltinBinding(expr: TransformScriptBindingExpr, ctx: TransformScriptBindingContext) {
  const nowUtc = getNowParts({ epochMilliseconds: ctx.nowEpochMilliseconds });
  const nowLocal = getNowParts({ local: true, epochMilliseconds: ctx.nowEpochMilliseconds });

  switch (expr.name) {
    case 'date':
      return formatIsoDate(nowUtc);
    case 'datetime':
      return formatIsoDateTime(nowUtc);
    case 'now':
      return formatIsoDateTimeMs(nowUtc);
    case 'datelocal':
      return formatIsoDate(nowLocal);
    case 'datetimelocal':
      return formatIsoDateTime(nowLocal);
    case 'nowlocal':
      return formatIsoDateTimeMs(nowLocal);
    case 'random': {
      switch (expr.args.length) {
        case 0:
          return randomString(10);
        case 1:
          return randomString(parsePositiveInt(expr.args[0] ?? ''));
        case 2: {
          const min = parsePositiveInt(expr.args[0] ?? '');
          const max = parsePositiveInt(expr.args[1] ?? '');
          if (min <= 0 || max < min) {
            return expr.raw;
          }
          return randomString(randomLengthInRange(min, max));
        }
        default:
          return expr.raw;
      }
    }
    default:
      return expr.raw;
  }
}

export function parseTransformScriptBinding(value: string): TransformScriptBindingExpr | null {
  if (!value.startsWith('{{') || !value.endsWith('}}')) {
    return null;
  }

  const body = value.slice(2, -2).trim();
  if (!body || body.includes('{{') || body.includes('}}')) {
    return null;
  }

  const parts = body.split(':', 2);
  const name = (parts[0] ?? '').trim();
  if (!name) {
    return null;
  }

  return {
    name,
    args: parts[1] ? parts[1].split(',').map(item => item.trim()) : [],
    raw: value
  };
}

export function resolveTransformScriptBinding(value: string, context: TransformScriptBindingContext) {
  const expr = parseTransformScriptBinding(value);
  if (!expr) {
    return value;
  }

  if (expr.name === '$_self') {
    return context.hasSelf ? context.selfValue : expr.raw;
  }

  if (expr.name.startsWith('$')) {
    const refName = expr.name.slice(1);
    return refName in context.refs ? (context.refs[refName] ?? '') : expr.raw;
  }

  return resolveBuiltinBinding(expr, context);
}

export function resolveTransformScriptBindingsInTemplate(value: string, context: TransformScriptBindingContext) {
  if (!value) {
    return value;
  }

  const directExpr = parseTransformScriptBinding(value);
  if (directExpr) {
    return resolveTransformScriptBinding(value, context);
  }

  let remaining = value;
  let output = '';
  let replaced = false;

  for (;;) {
    const start = remaining.indexOf('{{');
    if (start < 0) {
      output += remaining;
      break;
    }

    const endOffset = remaining.indexOf('}}', start + 2);
    if (endOffset < 0) {
      output += remaining;
      break;
    }

    output += remaining.slice(0, start);
    const rawExpr = remaining.slice(start, endOffset + 2);
    const resolved = resolveTransformScriptBinding(rawExpr, context);
    if (resolved !== rawExpr) {
      replaced = true;
    }
    output += resolved;
    remaining = remaining.slice(endOffset + 2);
  }

  return replaced ? output : value;
}
